import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  ensureMixpanelBootstrapScript,
  getMixpanelRegistrationProperties,
  mixpanelSampler,
  shouldInitializeMixpanel,
} from "@lending/lib/mixpanel";
import { Institution } from "@lending/types";

const MIXPANEL_INITIALIZATION_RETRY_LIMIT = 20;

function useInitializeMixpanel(institution: Institution) {
  useEffect(() => {
    const mixpanelProjectToken = institution.mixpanel_project_token;

    const captureMixpanelSentryContext = (stage: string, callback: () => void) => {
      Sentry.withScope((scope) => {
        scope.setTag("mixpanel_stage", stage);
        scope.setExtra("window_mixpanel_type", typeof window.mixpanel);
        scope.setExtra("window_mixpanel_constructor", window.mixpanel?.constructor?.name ?? null);
        callback();
      });
    };

    if (!mixpanelProjectToken || !shouldInitializeMixpanel()) {
      return;
    }

    try {
      ensureMixpanelBootstrapScript();
    } catch (error) {
      captureMixpanelSentryContext("bootstrap_injection", () => {
        Sentry.captureException(error);
      });
      return;
    }

    let timeoutId: number | undefined;
    let retryCount = 0;

    const initializeMixpanel = () => {
      if (typeof window.mixpanel?.init !== "function") {
        if (retryCount >= MIXPANEL_INITIALIZATION_RETRY_LIMIT) {
          captureMixpanelSentryContext("bootstrap_timeout", () => {
            Sentry.captureMessage(
              "Lending Mixpanel bootstrap did not expose init before retry limit"
            );
          });
          return;
        }

        retryCount += 1;
        timeoutId = window.setTimeout(initializeMixpanel, 100);
        return;
      }

      try {
        if (institution.force_complete_mixpanel_sampling || mixpanelSampler()) {
          window.mixpanel.init(mixpanelProjectToken, {
            autocapture: {
              capture_extra_attrs: ["data-testid"],
            },
          });
          window.mixpanel.register_once(
            getMixpanelRegistrationProperties(institution.internal_name)
          );
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    initializeMixpanel();

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    institution.force_complete_mixpanel_sampling,
    institution.internal_name,
    institution.mixpanel_project_token,
  ]);
}

export { useInitializeMixpanel };
